"use client";

import { createContext, useCallback, useContext, useRef, useState } from "react";
import type { Event } from "@/lib/types";
import type { JukeboxStart } from "@/lib/jukebox-links";
import type { JukeboxEvent } from "@/lib/jukebox-playlist";
import { captureDiscoveryEvent } from "@/lib/discovery/analytics";

/** A bill a card asks the page's jukebox to open on, leading with one of its acts. */
export interface JukeboxLaunchTarget {
  event: Event;
  artistId?: number;
}
type JukeboxLauncher = (target: JukeboxLaunchTarget) => void;
interface JukeboxLabels { open: string; reopen: string }
const DEFAULT_JUKEBOX_LABELS: JukeboxLabels = { open: "Open in jukebox", reopen: "Reopen jukebox" };

interface SpotifyPlayerState {
  artistId: string | null;
  artistName: string | null;
  playbackTarget: JukeboxLaunchTarget | null;
}

export interface RelatedJukeboxSession {
  citySlug: string;
  start: JukeboxStart<JukeboxEvent>;
  target: JukeboxLaunchTarget;
}

const EMPTY_PLAYBACK: SpotifyPlayerState = { artistId: null, artistName: null, playbackTarget: null };

interface SpotifyPlayerContextValue extends SpotifyPlayerState {
  citySlugRef: React.RefObject<string>;
  play: (artistId: string, artistName: string, target?: JukeboxLaunchTarget) => void;
  startPlayback: (artistId: string, artistName: string, target?: JukeboxLaunchTarget) => void;
  close: () => void;
  setCitySlug: (slug: string) => void;
  jukeboxOpen: boolean;
  setJukeboxOpen: (open: boolean) => void;
  reopenJukebox: (() => void) | null;
  registerJukeboxReopen: (reopen: () => void) => () => void;
  /** Set while the page hosts a jukebox that can open on a given bill. */
  launchJukebox: JukeboxLauncher | null;
  registerJukeboxLauncher: (launch: JukeboxLauncher, labels: JukeboxLabels) => () => void;
  jukeboxLabels: JukeboxLabels;
  jukeboxMatchesPlayback: boolean;
  relatedSession: RelatedJukeboxSession | null;
  setRelatedSession: (session: RelatedJukeboxSession) => void;
}

const SpotifyPlayerContext = createContext<SpotifyPlayerContextValue>({
  artistId: null,
  artistName: null,
  playbackTarget: null,
  citySlugRef: { current: "" },
  play: () => {},
  startPlayback: () => {},
  close: () => {},
  setCitySlug: () => {},
  jukeboxOpen: false,
  setJukeboxOpen: () => {},
  reopenJukebox: null,
  registerJukeboxReopen: () => () => {},
  launchJukebox: null,
  registerJukeboxLauncher: () => () => {},
  jukeboxLabels: DEFAULT_JUKEBOX_LABELS,
  jukeboxMatchesPlayback: false,
  relatedSession: null,
  setRelatedSession: () => {},
});

export function SpotifyPlayerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<SpotifyPlayerState>(EMPTY_PLAYBACK);
  const stateRef = useRef(state);
  const [jukeboxOpen, updateJukeboxOpen] = useState(false);
  const jukeboxOpenRef = useRef(false);
  const [jukeboxPlayback, setJukeboxPlayback] = useState<SpotifyPlayerState | null>(null);
  const [relatedSession, setRelatedSession] = useState<RelatedJukeboxSession | null>(null);
  const setJukeboxOpen = useCallback((open: boolean) => {
    if (open && !jukeboxOpenRef.current) captureDiscoveryEvent("discovery_player_opened", { event_id: stateRef.current.playbackTarget?.event.id });
    jukeboxOpenRef.current = open;
    updateJukeboxOpen(open);
    if (open) setJukeboxPlayback(stateRef.current);
  }, []);
  const [reopenJukebox, setReopenJukebox] = useState<(() => void) | null>(null);

  const registerJukeboxReopen = useCallback((reopen: () => void) => {
    setReopenJukebox(() => reopen);
    return () => {
      setReopenJukebox((current) => current === reopen ? null : current);
    };
  }, []);

  const [launchJukebox, setLaunchJukebox] = useState<JukeboxLauncher | null>(null);
  const [jukeboxLabels, setJukeboxLabels] = useState(DEFAULT_JUKEBOX_LABELS);

  const registerJukeboxLauncher = useCallback((launch: JukeboxLauncher, labels: JukeboxLabels) => {
    setLaunchJukebox(() => launch);
    setJukeboxLabels(labels);
    return () => {
      setLaunchJukebox((current: JukeboxLauncher | null) => current === launch ? null : current);
    };
  }, []);

  const citySlugRef = useRef("");

  const setCitySlug = useCallback((slug: string) => {
    citySlugRef.current = slug;
  }, []);

  const updatePlayback = useCallback((next: SpotifyPlayerState) => {
    if (next.artistId && (next.artistId !== stateRef.current.artistId || next.playbackTarget?.event.id !== stateRef.current.playbackTarget?.event.id)) {
      captureDiscoveryEvent("discovery_listen_requested", { event_id: next.playbackTarget?.event.id, artist_id: next.playbackTarget?.artistId });
    }
    stateRef.current = next;
    setState(next);
    if (jukeboxOpenRef.current) setJukeboxPlayback(next);
  }, []);

  const startPlayback = useCallback((artistId: string, artistName: string, target?: JukeboxLaunchTarget) => {
    updatePlayback({ artistId, artistName, playbackTarget: target ?? null });
  }, [updatePlayback]);

  const play = useCallback((artistId: string, artistName: string, target?: JukeboxLaunchTarget) => {
    updatePlayback(stateRef.current.artistId === artistId ? EMPTY_PLAYBACK : { artistId, artistName, playbackTarget: target ?? null });
  }, [updatePlayback]);

  const close = useCallback(() => updatePlayback(EMPTY_PLAYBACK), [updatePlayback]);
  const jukeboxMatchesPlayback = !!state.artistId && jukeboxPlayback?.artistId === state.artistId
    && jukeboxPlayback?.playbackTarget?.event.id === state.playbackTarget?.event.id;

  return (
    <SpotifyPlayerContext.Provider value={{ ...state, citySlugRef, play, startPlayback, close, setCitySlug, jukeboxOpen, setJukeboxOpen, reopenJukebox, registerJukeboxReopen, launchJukebox, registerJukeboxLauncher, jukeboxLabels, jukeboxMatchesPlayback, relatedSession, setRelatedSession }}>
      {children}
    </SpotifyPlayerContext.Provider>
  );
}

export function useSpotifyPlayer() {
  return useContext(SpotifyPlayerContext);
}
